import { mutationOptions, type QueryClient } from '@tanstack/react-query';
import { api } from '#proto/index.qonto.account_management';
import { camelizeKeys, camelToSnakeCaseShallow, client, type QontoScaClient } from '#queries/utils';
import { deactivationQueries } from './queries';
import type {
  CheckDeactivationRequirementsResult,
  CreateDeactivationParams,
  CreateDeactivationResult,
} from './types';

export interface DeactivationMutationContext {
  client: QueryClient;
}

const { ApiCreateDowngradeResponse } = api.organizations.deactivations.firstparty.v3;

function createDowngrade() {
  return mutationOptions({
    mutationFn: async (organizationId: string) => {
      const response = await client(`/v3/organizations/${organizationId}/deactivation/downgrade`, {
        method: 'POST',
        body: { organization_id: organizationId },
      });
      return ApiCreateDowngradeResponse.fromJSON(response);
    },
    onSuccess: (_data, variables, _result, context) => {
      return context.client.invalidateQueries({
        queryKey: deactivationQueries.item(variables).queryKey,
      });
    },
  });
}

async function cancelDowngradeRequest(organizationId: string): Promise<void> {
  await client(`/v3/organizations/${organizationId}/deactivation/downgrade`, {
    method: 'DELETE',
  });
}

function cancelDowngrade() {
  return mutationOptions({
    mutationFn: cancelDowngradeRequest,
    onSuccess: (_data, variables, _result, context) => {
      return context.client.invalidateQueries({
        queryKey: deactivationQueries.item(variables).queryKey,
      });
    },
  });
}

async function cancelDeactivationRequest(organizationId: string): Promise<void> {
  await client(`/v3/organizations/${organizationId}/deactivation`, {
    method: 'DELETE',
  });
}

function cancelDeactivation(organizationId: string) {
  return mutationOptions({
    mutationFn: () => cancelDeactivationRequest(organizationId),
    onSuccess: async (_data, _variables, _result, context) => {
      await context.client.resetQueries({
        queryKey: deactivationQueries.item(organizationId).queryKey,
      });
    },
  });
}

function checkRequirements(organizationId: string) {
  return mutationOptions({
    mutationFn: async (): Promise<CheckDeactivationRequirementsResult> => {
      const response = await client<{
        valid: boolean;
        provisional_due_date: string | null;
        account_authorized_balance_zero: boolean;
      }>(`/v3/organizations/${organizationId}/deactivation_requirements`);
      return camelizeKeys(response);
    },
  });
}

function redeemVoucher(organizationId: string) {
  return mutationOptions({
    mutationFn: () =>
      client(`/v3/organizations/${organizationId}/deactivation_voucher_redeem`, {
        method: 'POST',
      }),
  });
}

function createDeactivation(organizationId: string, scaClient: QontoScaClient) {
  return mutationOptions({
    mutationFn: (params: CreateDeactivationParams): Promise<CreateDeactivationResult> =>
      scaClient<{ due_date: string }>(`/v3/organizations/${organizationId}/deactivation`, {
        method: 'POST',
        body: camelToSnakeCaseShallow(params),
      }),
  });
}

interface CsCallbackInput {
  organizationId: string;
  slotPreference: 'AM' | 'PM';
  // Required by the backend (422 if missing or invalid).
  closingReasonCategory: string;
  closingReasonSubCategory: string;
  // Optional per the backend contract; only present when the user has selected a sub-detail.
  closingReasonDetails?: string;
  closingReasonDetailsOther?: string;
}

async function csCallbackRequest({
  organizationId,
  slotPreference,
  closingReasonCategory,
  closingReasonSubCategory,
  closingReasonDetails,
  closingReasonDetailsOther,
}: CsCallbackInput): Promise<void> {
  // No proto definition exists for this request body yet; fields are sent as plain JSON.
  await client(`/v3/organizations/${organizationId}/deactivation/cs_callback`, {
    method: 'POST',
    body: {
      deactivation_reason: {
        closing_reason_category: closingReasonCategory,
        closing_reason_sub_category: closingReasonSubCategory,
        closing_reason_details: closingReasonDetails,
        closing_reason_details_other: closingReasonDetailsOther,
      },
      slot_preference: slotPreference,
    },
  });
}

function csCallback() {
  return mutationOptions({
    mutationFn: csCallbackRequest,
  });
}

export const deactivationMutations = {
  createDowngrade,
  cancelDowngrade,
  cancelDeactivation,
  checkRequirements,
  redeemVoucher,
  createDeactivation,
  csCallback,
};
